import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import TimezoneSelector from '../timezone';

interface SMBFormData {
  name: string;
  slug: string;
  display_name: string;
  description: string;
  business_type: string;
  industry: string;
  vertical: string;
  owner_name: string;
  owner_email: string;
  phone: string;
  whatsapp_phone: string;
  email: string;
  website: string;
  base_service_price: number;
  currency: string;
  timezone: string;
  business_hours_start: string;
  business_hours_end: string;
  whatsapp_enabled: boolean;
  instagram_enabled: boolean;
  auto_reply_enabled: boolean;
  qualification_flow_enabled: boolean;
  reminder_enabled: boolean;
}

interface SMBFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: SMBFormData) => Promise<void>;
  editingSMB?: SMBFormData | null;
  loading?: boolean;
  agencyId?: string;
}

const SMBFormDialog: React.FC<SMBFormDialogProps> = ({
  open,
  onClose,
  onSave,
  editingSMB,
  loading = false
}) => {
  const [formData, setFormData] = useState<SMBFormData>({
    name: '',
    slug: '',
    display_name: '',
    description: '',
    business_type: 'service',
    industry: '',
    vertical: '',
    owner_name: '',
    owner_email: '',
    phone: '',
    whatsapp_phone: '',
    email: '',
    website: '',
    base_service_price: 100,
    currency: 'INR',
    timezone: 'IST',
    business_hours_start: '09:00',
    business_hours_end: '18:00',
    whatsapp_enabled: true,
    instagram_enabled: false,
    auto_reply_enabled: true,
    qualification_flow_enabled: true,
    reminder_enabled: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (editingSMB) {
      setFormData(editingSMB);
    } else {
      setFormData({
        name: '',
        slug: '',
        display_name: '',
        description: '',
        business_type: 'service',
        industry: '',
        vertical: '',
        owner_name: '',
        owner_email: '',
        phone: '',
        whatsapp_phone: '',
        email: '',
        website: '',
        base_service_price: 100,
        currency: 'USD',
        timezone: 'America/New_York',
        business_hours_start: '09:00',
        business_hours_end: '18:00',
        whatsapp_enabled: true,
        instagram_enabled: false,
        auto_reply_enabled: true,
        qualification_flow_enabled: true,
        reminder_enabled: true
      });
    }
    setErrors({});
  }, [editingSMB, open]);

  const handleInputChange = (field: keyof SMBFormData, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from name
    if (field === 'name' && !editingSMB) {
      const slug = value.toString()
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .substring(0, 50);
      setFormData(prev => ({ ...prev, slug }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Business name is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!formData.business_type) newErrors.business_type = 'Business type is required';
    if (!formData.industry) newErrors.industry = 'Industry is required';
    if (!formData.owner_name.trim()) newErrors.owner_name = 'Owner name is required';
    if (!formData.owner_email.trim()) newErrors.owner_email = 'Owner email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.owner_email)) newErrors.owner_email = 'Invalid email format';
    if (formData.base_service_price < 0) newErrors.base_service_price = 'Price must be positive';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving SMB:', error);
    }
  };

  const businessTypes = [
    { value: 'service', label: 'Service Business' },
    { value: 'retail', label: 'Retail' },
    { value: 'hospitality', label: 'Hospitality' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'professional', label: 'Professional Services' },
    { value: 'ecommerce', label: 'E-commerce' }
  ];

  const industries = [
    { value: 'hotel', label: 'Hotel' },
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'clinic', label: 'Medical Clinic' },
    { value: 'coaching', label: 'Coaching' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'fitness', label: 'Fitness' },
    { value: 'beauty', label: 'Beauty & Wellness' },
    { value: 'education', label: 'Education' }
  ];

  const verticals = [
    { value: 'luxury_hotel', label: 'Luxury Hotel' },
    { value: 'boutique_hotel', label: 'Boutique Hotel' },
    { value: 'medical_clinic', label: 'Medical Clinic' },
    { value: 'dental_clinic', label: 'Dental Clinic' },
    { value: 'life_coaching', label: 'Life Coaching' },
    { value: 'business_coaching', label: 'Business Coaching' },
    { value: 'fitness_coaching', label: 'Fitness Coaching' }
  ];

  const currencies = [
    { value: 'USD', label: 'USD ($)' },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'GBP', label: 'GBP (£)' },
    { value: 'CAD', label: 'CAD (C$)' },
    { value: 'INR', label: 'INR (₹)' }

  ];


  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {editingSMB ? 'Edit Business' : 'Create New Business'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Slug"
                value={formData.slug}
                onChange={(e) => handleInputChange('slug', e.target.value)}
                error={!!errors.slug}
                helperText={errors.slug || 'URL-friendly identifier'}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                value={formData.display_name}
                onChange={(e) => handleInputChange('display_name', e.target.value)}
                helperText="Public display name"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
                helperText="Brief description of the business"
              />
            </Grid>

            {/* Business Type */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={!!errors.business_type}>
                <InputLabel>Business Type</InputLabel>
                <Select
                  value={formData.business_type}
                  onChange={(e) => handleInputChange('business_type', e.target.value)}
                  label="Business Type"
                >
                  {businessTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.business_type && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.business_type}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={!!errors.industry}>
                <InputLabel>Industry</InputLabel>
                <Select
                  value={formData.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  label="Industry"
                >
                  {industries.map((industry) => (
                    <MenuItem key={industry.value} value={industry.value}>
                      {industry.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.industry && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.industry}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Vertical</InputLabel>
                <Select
                  value={formData.vertical}
                  onChange={(e) => handleInputChange('vertical', e.target.value)}
                  label="Vertical"
                >
                  {verticals.map((vertical) => (
                    <MenuItem key={vertical.value} value={vertical.value}>
                      {vertical.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Owner Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Owner Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Owner Name"
                value={formData.owner_name}
                onChange={(e) => handleInputChange('owner_name', e.target.value)}
                error={!!errors.owner_name}
                helperText={errors.owner_name}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Owner Email"
                type="email"
                value={formData.owner_email}
                onChange={(e) => handleInputChange('owner_email', e.target.value)}
                error={!!errors.owner_email}
                helperText={errors.owner_email}
                required
              />
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Contact Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                helperText="Primary business phone"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="WhatsApp Phone"
                value={formData.whatsapp_phone}
                onChange={(e) => handleInputChange('whatsapp_phone', e.target.value)}
                helperText="WhatsApp Business phone"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                helperText="Business email"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                helperText="Business website URL"
              />
            </Grid>

            {/* Pricing & Business Hours */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Pricing & Business Hours
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Base Service Price"
                type="number"
                value={formData.base_service_price}
                onChange={(e) => handleInputChange('base_service_price', parseFloat(e.target.value) || 0)}
                error={!!errors.base_service_price}
                helperText={errors.base_service_price || 'Starting price for services'}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  label="Currency"
                >
                  {currencies.map((currency) => (
                    <MenuItem key={currency.value} value={currency.value}>
                      {currency.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TimezoneSelector
                value={formData.timezone}
                onChange={(value) => handleInputChange('timezone', value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business Hours Start"
                type="time"
                value={formData.business_hours_start}
                onChange={(e) => handleInputChange('business_hours_start', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business Hours End"
                type="time"
                value={formData.business_hours_end}
                onChange={(e) => handleInputChange('business_hours_end', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Features */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Features & Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.whatsapp_enabled}
                    onChange={(e) => handleInputChange('whatsapp_enabled', e.target.checked)}
                  />
                }
                label="WhatsApp Integration"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.instagram_enabled}
                    onChange={(e) => handleInputChange('instagram_enabled', e.target.checked)}
                  />
                }
                label="Instagram Integration"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.auto_reply_enabled}
                    onChange={(e) => handleInputChange('auto_reply_enabled', e.target.checked)}
                  />
                }
                label="Auto Reply"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.qualification_flow_enabled}
                    onChange={(e) => handleInputChange('qualification_flow_enabled', e.target.checked)}
                  />
                }
                label="Lead Qualification Flow"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.reminder_enabled}
                    onChange={(e) => handleInputChange('reminder_enabled', e.target.checked)}
                  />
                }
                label="Reminder Notifications"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Saving...' : editingSMB ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SMBFormDialog;
